import { ALLIANCE_BUILDING, BUILDING_NAMES, DEFAULT_SCIENCES, OZONE_MAX, TEAM_PLANET_ECONOMY_MULTIPLIER, TEAM_PLANET_POPULATION_CAPACITY, TEAM_PLANET_REQUIRED_BUILDING_LEVEL, getShipDefinition, } from "@kaeliux/shared";
import { highestAllianceBuildingLevel } from "./alliance.js";
import { applyDerivedStats, initialPlanetBuildings } from "./derivedStats.js";
import { calculateMaxActiveFlights } from "./flightEngine.js";
import { calculateDockedPopulation } from "./ships.js";
/**
 * Pure Team-planet rules: the conversion prerequisite, the conversion
 * itself (an existing planet reset in place), and the shipyard's per-race
 * lane selection. Mirrors `alliance.ts`'s split — `apps/functions/src/
 * teamPlanets.ts` only does Firestore I/O and transaction bookkeeping
 * around these decisions.
 *
 * Deliberately NOT a parallel engine: a Team planet IS a `Planet`
 * (`Planet.allianceId` set), converted in place from one of the leader's
 * own planets — see that field's doc comment
 * (`packages/shared/src/types/planet.types.ts`). Building/science/ship
 * queueing therefore reuse the EXACT SAME `tickEvents.ts` functions every
 * normal planet uses (`planBuildingQueueEntry`, `cancelBuildingEvent`,
 * `planScienceQueueEntry`, `cancelScienceEvent`, `stageShipBuildItem`,
 * `cancelShipBuildEvent`, `replayEventsToNow`), which now accept an
 * optional `economyMultiplier`/`ozoneExempt`/lane-override for exactly this
 * purpose — see those functions' doc comments. This file only holds the
 * genuinely NEW rules those can't express.
 */
const ALLOWED = { allowed: true };
function denied(reason) {
    return { allowed: false, reason };
}
/**
 * Whether the leader may convert ONE SPECIFIC planet into their alliance's
 * Team planet. The Team-building prerequisite may be satisfied by ANY of
 * the leader's planets (same "any owned planet qualifies" rule alliance
 * founding uses) — it does NOT have to be the planet being converted.
 */
export function canConvertToTeamPlanet(allLeaderPlanets, targetHasActiveFlights, allianceAlreadyHasTeamPlanet) {
    if (allianceAlreadyHasTeamPlanet)
        return denied("This alliance already has a Team planet");
    if (targetHasActiveFlights) {
        return denied("This planet has active flights to or from it — wait for them to finish first");
    }
    const level = highestAllianceBuildingLevel(allLeaderPlanets);
    if (level < TEAM_PLANET_REQUIRED_BUILDING_LEVEL) {
        return denied(`Converting a planet into the Team planet requires the ${BUILDING_NAMES[ALLIANCE_BUILDING]} at Level ${TEAM_PLANET_REQUIRED_BUILDING_LEVEL} on one of your planets`);
    }
    return ALLOWED;
}
/**
 * Convert an existing planet into its alliance's Team planet — RESETS
 * buildings, resources, ships, and the build/research/ship queue entirely
 * (2026-08-14 gameplay ruling), while keeping the planet's identity
 * (`id`, `coordinates`, `name`, `planetType`, `createdAt`) unchanged. Sets
 * `allianceId` and a fresh all-zero `sciences` map. Population capacity is
 * fixed at `TEAM_PLANET_POPULATION_CAPACITY` from this point on, replacing
 * the normal Launcher-derived formula.
 */
export function convertPlanetToTeamPlanet(planet, allianceId, now) {
    const reset = {
        ...planet,
        allianceId,
        sciences: { ...DEFAULT_SCIENCES },
        teamShipUpgrades: {},
        buildings: initialPlanetBuildings(),
        resources: {
            metal: { current: 0, capacity: 0, rate: 0 },
            minerals: { current: 0, capacity: 0, rate: 0 },
            gas: { current: 0, capacity: 0, rate: 0 },
            energy: { current: 0, rate: 0, consumed: 0 },
            scrap: { current: 0 },
            population: { capacity: TEAM_PLANET_POPULATION_CAPACITY },
        },
        ships: {},
        shipsInFlight: {},
        tickCheckpoints: [],
        nextEventId: 1,
        scrapRecycleDaily: undefined,
        lastSpyScanAt: undefined,
        state: { ozone: { current: OZONE_MAX }, lastTickAt: now },
        updatedAt: now,
    };
    return applyDerivedStats(reset, 0, 0, {
        economyMultiplier: TEAM_PLANET_ECONOMY_MULTIPLIER,
        populationCapacityOverride: TEAM_PLANET_POPULATION_CAPACITY,
    });
}
/**
 * One race's ship upgrade levels on a Team planet, keyed by numeric ship id —
 * the same shape `resolveShipUpgrades` (functions) gives for a player, so
 * battle and flight code reads either through one path.
 */
export function resolveTeamPlanetShipUpgrades(planet, race) {
    const result = {};
    for (const [shipId, level] of Object.entries(planet.teamShipUpgrades?.[race] ?? {})) {
        result[Number(shipId)] = level;
    }
    return result;
}
/**
 * Fold every applied Team planet `shipUpgrade` record (those carrying a
 * `queueLane`) into the per-race levels. Both the Cloud Functions replay and
 * the client mock replay call this, so the two never disagree on how a
 * resolved upgrade raises a level. Never mutates `current`.
 */
export function applyResolvedTeamPlanetShipUpgrades(current, applied) {
    const result = {};
    for (const [race, levels] of Object.entries(current ?? {})) {
        result[race] = { ...levels };
    }
    for (const record of applied) {
        const event = record.event;
        if (event.eventType !== "shipUpgrade")
            continue;
        const lane = event.eventDetails.queueLane;
        if (!lane)
            continue;
        const existing = result[lane]?.[event.eventDetails.shipType] ?? 0;
        result[lane] = { ...result[lane], [event.eventDetails.shipType]: Math.max(existing, event.eventDetails.targetLevel) };
    }
    return result;
}
/**
 * The sciences and per-race ship upgrade levels a Team planet has once its
 * pending events up to `at` are applied: the stored values with every
 * non-cancelled `scienceResearch` and laned `shipUpgrade` event ending at or
 * before `at` folded on. Battles call this with the fleet's arrival time so an
 * upgrade that finished before the fleet landed counts even if no replay has
 * persisted it yet. It cannot subtract progress already persisted, so an event
 * ending after `at` still counts when a replay applied it before the call.
 * Pure — never mutates `planet`.
 */
export function resolveTeamPlanetProgressAt(planet, at) {
    const due = (planet.tickCheckpoints ?? [])
        .filter((e) => !e.cancelled && e.endDate.getTime() <= at.getTime())
        .sort((a, b) => a.endDate.getTime() - b.endDate.getTime() || a.eventId - b.eventId);
    const sciences = { ...(planet.sciences ?? DEFAULT_SCIENCES) };
    const applied = [];
    for (const event of due) {
        if (event.eventType === "scienceResearch") {
            sciences[event.eventDetails.scienceId] = event.eventDetails.targetLevel;
        }
        else if (event.eventType === "shipUpgrade") {
            applied.push({ event, status: "applied", resolvedAt: event.endDate });
        }
    }
    return { sciences, teamShipUpgrades: applyResolvedTeamPlanetShipUpgrades(planet.teamShipUpgrades, applied) };
}
/**
 * The Team planet shipyard is three independent per-race FIFOs
 * (`TEAM_PLANET_SHIP_BUILD_LANES`) rather than the normal
 * race/mutation-derived lane (`computeShipBuildLane`) — every ship a given
 * player queues, of any type, chains behind that SAME race's other pending
 * batches only.
 */
export function computeTeamPlanetShipBuildLane(race) {
    return race;
}
// ---------------------------------------------------------------------------
// Team-planet race-tagged ship ledger (`Planet.teamShips`)
//
// A Team planet's docked and in-flight ships come from multiple races, each
// with different hangar values for the same ship ID (e.g. ship 9: Humans=700,
// Robots=615, Axons=320). The `teamShips` ledger tracks per-race counts so
// population can be computed correctly; the flat `planet.ships` /
// `planet.shipsInFlight` maps hold the per-id totals across ALL races and
// are recomputed from the ledger by every mutating helper below — they are
// never written on their own for a team planet.
// ---------------------------------------------------------------------------
const EMPTY_RACE_SHIPS = { docked: {}, inFlight: {} };
function getRaceEntry(ledger, race) {
    return ledger?.[race] ?? EMPTY_RACE_SHIPS;
}
function addToShipMap(map, shipId, delta) {
    return { ...map, [shipId]: Math.max(0, (map[shipId] ?? 0) + delta) };
}
function recomputeTotals(ledger) {
    const ships = {};
    const inFlight = {};
    for (const entry of Object.values(ledger)) {
        if (!entry)
            continue;
        for (const [k, v] of Object.entries(entry.docked)) {
            if (v)
                ships[Number(k)] = (ships[Number(k)] ?? 0) + v;
        }
        for (const [k, v] of Object.entries(entry.inFlight)) {
            if (v)
                inFlight[Number(k)] = (inFlight[Number(k)] ?? 0) + v;
        }
    }
    return { ships: ships, shipsInFlight: inFlight };
}
export function teamPlanetDockShips(planet, race, shipId, quantity) {
    if (quantity <= 0)
        return planet;
    const entry = getRaceEntry(planet.teamShips, race);
    const ledger = {
        ...planet.teamShips,
        [race]: { ...entry, docked: addToShipMap(entry.docked, shipId, quantity) },
    };
    const totals = recomputeTotals(ledger);
    return { ...planet, teamShips: ledger, ships: totals.ships, shipsInFlight: totals.shipsInFlight };
}
export function teamPlanetUndockShips(planet, race, shipId, quantity) {
    if (quantity <= 0)
        return planet;
    const entry = getRaceEntry(planet.teamShips, race);
    const ledger = {
        ...planet.teamShips,
        [race]: { ...entry, docked: addToShipMap(entry.docked, shipId, -quantity) },
    };
    const totals = recomputeTotals(ledger);
    return { ...planet, teamShips: ledger, ships: totals.ships, shipsInFlight: totals.shipsInFlight };
}
export function teamPlanetDispatchShips(planet, race, ships) {
    const entry = getRaceEntry(planet.teamShips, race);
    const docked = { ...entry.docked };
    const inFl = { ...entry.inFlight };
    for (const [k, count] of Object.entries(ships)) {
        if (!count)
            continue;
        const id = Number(k);
        docked[id] = Math.max(0, (docked[id] ?? 0) - count);
        inFl[id] = (inFl[id] ?? 0) + count;
    }
    const ledger = { ...planet.teamShips, [race]: { docked, inFlight: inFl } };
    const totals = recomputeTotals(ledger);
    return { ...planet, teamShips: ledger, ships: totals.ships, shipsInFlight: totals.shipsInFlight };
}
export function teamPlanetReturnShips(planet, race, ships) {
    const entry = getRaceEntry(planet.teamShips, race);
    const docked = { ...entry.docked };
    const inFl = { ...entry.inFlight };
    for (const [k, count] of Object.entries(ships)) {
        if (!count)
            continue;
        const id = Number(k);
        docked[id] = (docked[id] ?? 0) + count;
        inFl[id] = Math.max(0, (inFl[id] ?? 0) - count);
    }
    const ledger = { ...planet.teamShips, [race]: { docked, inFlight: inFl } };
    const totals = recomputeTotals(ledger);
    return { ...planet, teamShips: ledger, ships: totals.ships, shipsInFlight: totals.shipsInFlight };
}
/**
 * Ships leaving a Sun's garrison for the Team planet: they re-enter the
 * race's in-flight ledger (counting against the Team planet's population
 * again) until `teamPlanetReturnShips` docks them.
 */
export function teamPlanetAddInFlight(planet, race, ships) {
    const entry = getRaceEntry(planet.teamShips, race);
    const inFl = { ...entry.inFlight };
    for (const [k, count] of Object.entries(ships)) {
        if (!count)
            continue;
        const id = Number(k);
        inFl[id] = (inFl[id] ?? 0) + count;
    }
    const ledger = { ...planet.teamShips, [race]: { ...entry, inFlight: inFl } };
    const totals = recomputeTotals(ledger);
    return { ...planet, teamShips: ledger, ships: totals.ships, shipsInFlight: totals.shipsInFlight };
}
export function teamPlanetReleaseInFlight(planet, race, ships) {
    const entry = getRaceEntry(planet.teamShips, race);
    const inFl = { ...entry.inFlight };
    for (const [k, count] of Object.entries(ships)) {
        if (!count)
            continue;
        const id = Number(k);
        inFl[id] = Math.max(0, (inFl[id] ?? 0) - count);
    }
    const ledger = { ...planet.teamShips, [race]: { ...entry, inFlight: inFl } };
    const totals = recomputeTotals(ledger);
    return { ...planet, teamShips: ledger, ships: totals.ships, shipsInFlight: totals.shipsInFlight };
}
export function calculateTeamPlanetDockedPopulation(teamShips) {
    if (!teamShips)
        return 0;
    let total = 0;
    for (const [race, entry] of Object.entries(teamShips)) {
        if (!entry)
            continue;
        for (const [k, count] of Object.entries(entry.docked)) {
            if (!count)
                continue;
            total += getShipDefinition(race, Number(k)).hangar * count;
        }
    }
    return total;
}
export function calculateTeamPlanetInFlightPopulation(teamShips) {
    if (!teamShips)
        return 0;
    let total = 0;
    for (const [race, entry] of Object.entries(teamShips)) {
        if (!entry)
            continue;
        for (const [k, count] of Object.entries(entry.inFlight)) {
            if (!count)
                continue;
            total += getShipDefinition(race, Number(k)).hangar * count;
        }
    }
    return total;
}
function isPendingTeamShipBuild(e) {
    return e.eventType === "shipBuild" && !e.cancelled;
}
export function calculateTeamPlanetQueuedPopulation(pending) {
    let total = 0;
    for (const e of pending) {
        if (!isPendingTeamShipBuild(e))
            continue;
        const details = e.eventDetails;
        const race = (details.queueLane ?? "humans");
        total += getShipDefinition(race, details.shipType).hangar * details.quantity;
    }
    return total;
}
/** Docked + in flight + queued — the Team planet twin of `calculateUsedPopulation` (issue #294). */
export function calculateTeamPlanetUsedPopulation(teamShips, pending) {
    return calculateTeamPlanetDockedPopulation(teamShips)
        + calculateTeamPlanetInFlightPopulation(teamShips)
        + calculateTeamPlanetQueuedPopulation(pending);
}
export function calculateTeamPlanetFreePopulation(planet, pending) {
    return planet.resources.population.capacity - calculateTeamPlanetUsedPopulation(planet.teamShips, pending);
}
/**
 * Where `fleet` stands relative to the Team planet: `outbound` when it
 * departed from it (still away), `inbound` when it is a member's flight
 * still heading to it, `null` otherwise. Hostile Espionage is never
 * `inbound` — finding enemy probes is the counter-espionage scan's job.
 */
export function teamPlanetFlightDirection(fleet, teamPlanet, memberIds) {
    if (fleet.status === "completed")
        return null;
    if (fleet.originPlanetId === teamPlanet.id && (fleet.originOwnerId ?? fleet.userId) === teamPlanet.userId) {
        return "outbound";
    }
    const target = teamPlanet.coordinates;
    const headingIn = fleet.status === "outbound" &&
        fleet.target.galaxy === target.galaxy &&
        fleet.target.system === target.system &&
        fleet.target.position === target.position;
    return headingIn && fleet.mission !== "espionage" && memberIds.has(fleet.userId) ? "inbound" : null;
}
/**
 * The coordinator's view of `fleets` (any superset — irrelevant ones are
 * dropped): each flight priced by the race its ships belong to (a Team
 * planet fleet's `originRace`, otherwise its owner's race), plus the
 * population of every member's Station still on its way, which the HUD shows
 * as "(+N)", and the Team planet's own flight slots: every flight away from
 * it against its own Signal Systems, exactly as a player's are counted.
 */
export function buildTeamPlanetFlightsView(fleets, teamPlanet, members) {
    const memberIds = new Set(members.keys());
    const flights = [];
    for (const fleet of fleets) {
        const direction = teamPlanetFlightDirection(fleet, teamPlanet, memberIds);
        if (!direction)
            continue;
        const owner = members.get(fleet.userId);
        const race = fleet.originRace ?? owner?.race ?? "humans";
        flights.push({
            id: fleet.id,
            userId: fleet.userId,
            mission: fleet.mission,
            status: fleet.status,
            origin: fleet.origin,
            target: fleet.target,
            ships: fleet.ships,
            departedAt: fleet.departedAt,
            arrivesAt: fleet.arrivesAt,
            returnsAt: fleet.returnsAt,
            direction,
            ownerName: owner?.displayName ?? "Unknown Commander",
            race,
            population: calculateDockedPopulation(race, fleet.ships),
        });
    }
    const nextCheckpoint = (f) => (f.status === "outbound" ? f.arrivesAt : f.returnsAt).getTime();
    flights.sort((a, b) => nextCheckpoint(a) - nextCheckpoint(b) || a.id.localeCompare(b.id));
    return {
        flights,
        incomingStationPopulation: flights
            .filter((f) => f.direction === "inbound" && f.mission === "station")
            .reduce((sum, f) => sum + f.population, 0),
        activeCount: flights.filter((f) => f.direction === "outbound").length,
        maxActive: calculateMaxActiveFlights(teamPlanet.sciences?.signalSystems ?? 0),
    };
}
//# sourceMappingURL=teamPlanetEngine.js.map